import { Button, Message, Modal } from '@mairistem/composants';
import React from 'react';

import { REGISSEUR } from '@/constants';
import { RegieContext } from '@/contexts';
import { estEncaissementDirect } from '@/utils';
import { useRegieStore, useRegisseurStore } from '@/zustand';

const getSessionKey = (id: number | string) => `arrete-annuel-agent-${id}`;

/**
 * ArreteAnnuelAgentGuard
 *
 * Informe les agents de guichet que les encaissements ne sont pas encore autorisés
 * sur l'année en cours, quand l'arrêté annuel N-1 n'a pas encore été réalisé
 * (arreteAnnuelRequis = true). Le vrai blocage est côté serveur (encaissementPossible = false).
 *
 * La modal est fermable et stockée en sessionStorage par régie pour ne pas réapparaître
 * dans la même session. L'agent peut également changer de régie depuis la modal.
 */
const ArreteAnnuelAgentGuard = () => {
  const regie = useRegieStore((state) => state.regie);
  const { arreteAnnuelRequis, arreteAnnuelAnnee, identifiant } = regie || {};

  const connectedRegisseur = useRegisseurStore((state) => state.regisseur);
  const { mandat } = connectedRegisseur || {};

  const { showList } = React.useContext(RegieContext);

  const isAgent = mandat === REGISSEUR.TYPE_AGENT;
  // Année bloquée = exercice suivant celui à clôturer (arreteAnnuelAnnee, fourni par le back et
  // cohérent avec une fenêtre décalée). Repli sur l'année courante si l'info n'est pas disponible.
  const annee = arreteAnnuelAnnee != null ? arreteAnnuelAnnee + 1 : new Date().getFullYear();

  const [dismissed, setDismissed] = React.useState(() => {
    if (identifiant) {
      return sessionStorage.getItem(getSessionKey(identifiant)) === 'true';
    }
    return false;
  });

  React.useEffect(() => {
    if (identifiant) {
      setDismissed(sessionStorage.getItem(getSessionKey(identifiant)) === 'true');
    } else {
      setDismissed(false);
    }
  }, [identifiant]);

  const showModal = React.useMemo(
    () => !!arreteAnnuelRequis && isAgent && !dismissed && !estEncaissementDirect(),
    [arreteAnnuelRequis, isAgent, dismissed],
  );

  React.useEffect(() => {
    if (showModal && identifiant) {
      sessionStorage.setItem(getSessionKey(identifiant), 'true');
    }
  }, [showModal, identifiant]);

  const handleDismiss = React.useCallback(() => {
    setDismissed(true);
  }, []);

  const handleChangerRegie = React.useCallback(() => {
    setDismissed(true);
    showList?.();
  }, [showList]);

  if (!showModal) return null;

  return (
    <Modal open size="small">
      <Modal.Header>Information</Modal.Header>
      <Modal.Content>
        <Message warning>
          {`Les encaissements ne sont pas encore autorisés sur l'année ${annee}.`}
        </Message>
      </Modal.Content>
      <Modal.Actions>
        <Button content="Changer de régie" onClick={handleChangerRegie} basic />
        <Button content="Fermer" onClick={handleDismiss} primary />
      </Modal.Actions>
    </Modal>
  );
};

export default ArreteAnnuelAgentGuard;
